import { useState, useEffect } from 'react'
import Input from 'components/atoms/Input'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim } from 'lodash'

import { useTranslation } from 'react-i18next'

const AddFuelPolicyModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    fuelPolicy: '',
    fuelPolicyAr: '',
    active: true,
  })

  useEffect(() => {
    if (id) {
      fetchFuelPolicyData()
    }
  }, [id])

  const fetchFuelPolicyData = async () => {
    try {
      const response = await apiGet(`fuel-policy/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          fuelPolicy: data.fuelPolicy || '',
          fuelPolicyAr: data.fuelPolicyAr || '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addFuelPolicyModal.messages.failedToFetchFuelPolicyData'))
      }
    } catch (error) {
      console.error('Error fetching fuel policy data:', error)
      notify.error(t('addFuelPolicyModal.messages.failedToFetchFuelPolicyData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.fuelPolicy)) {
      notify.error(t('addFuelPolicyModal.messages.fuelPolicyNameRequired'))
      return
    }
    if (!trim(formData.fuelPolicyAr)) {
      notify.error(t('addFuelPolicyModal.messages.fuelPolicyArabicNameRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`fuel-policy/${id}`, formData)
        if (response.success) {
          notify.success(t('addFuelPolicyModal.messages.fuelPolicyUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('fuel-policy', formData)
        if (response.success) {
          notify.success(t('addFuelPolicyModal.messages.fuelPolicyAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        notify.error(response.message || t('addFuelPolicyModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving fuel policy:', error)
      notify.error(t('addFuelPolicyModal.messages.failedToSaveFuelPolicy'))
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      {/* Fuel Policy Name */}
      <div>
        <label htmlFor="fuelPolicy" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addFuelPolicyModal.labels.fuelPolicy')}
        </label>
        <input
          type="text"
          name="fuelPolicy"
          value={formData.fuelPolicy}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                     focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addFuelPolicyModal.placeholders.enterFuelPolicy')}
        />
      </div>

      {/* Fuel Policy Arabic Name */}
      <div>
        <label htmlFor="fuelPolicyAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addFuelPolicyModal.labels.fuelPolicyAr')}
        </label>
        <input
          type="text"
          name="fuelPolicyAr"
          value={formData.fuelPolicyAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                     focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addFuelPolicyModal.placeholders.enterFuelPolicyArabic')}
        />
      </div>

      {/* Active Switch */}
      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addFuelPolicyModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium 
                     text-gray-700 bg-white hover:bg-gray-50 focus:outline-none 
                     focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addFuelPolicyModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium 
                     text-white bg-dark-600 hover:bg-dark-700 focus:outline-none 
                     focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addFuelPolicyModal.buttons.updateFuelPolicy') : t('addFuelPolicyModal.buttons.addFuelPolicy')}
        </button>
      </div>
    </div>
  )
}

export default AddFuelPolicyModal
